"use client"

import { motion, useScroll, useTransform } from "motion/react"
import { ArrowLeft, Clock, Calendar, CalendarCheck } from "lucide-react"
import Link from "next/link"
import Image from "next/image"

import { Badge } from "@/core/ui/badge"
import {
  Breadcrumb,
  BreadcrumbItem,
  BreadcrumbLink,
  BreadcrumbSeparator,
} from "@/core/ui/breadcrumb"

interface GuideHeroProps {
  title: string
  subtitle?: string | null
  category?: string | null
  readTime?: string | null
  publishedAt?: string | null
  updatedAt?: string | null
  coverImage?: string | null
  coverImageAlt?: string | null
  tldr?: string | null
  backLink?: { href: string; label: string }
}

export function GuideHero(props: GuideHeroProps) {
  const { scrollY } = useScroll()
  const y = useTransform(scrollY, [0, 1000], [0, 400])
  const opacity = useTransform(scrollY, [0, 500], [1, 0])

  return (
    <div className="relative pt-32 pb-16 overflow-hidden">
      {/* Precision Grid Background covering just the hero zone */}
      <div 
        className="absolute inset-0 pointer-events-none opacity-40"
        style={{
          backgroundImage: `
            radial-gradient(ellipse 80% 100% at 50% -20%, transparent 20%, #050505 100%),
            linear-gradient(rgba(148,163,184,0.05) 1px, transparent 1px),
            linear-gradient(90deg, rgba(148,163,184,0.05) 1px, transparent 1px)
          `,
          backgroundSize: '100% 100%, 40px 40px, 40px 40px',
          backgroundPosition: 'center 0, center 0, center 0'
        }}
      />
      {/* Central Spotlight */}
      <div className="absolute top-0 left-1/2 -translate-x-1/2 w-[800px] h-[500px] bg-premium-cyan/10 blur-[120px] rounded-full pointer-events-none" />

      <div className="container relative z-10 px-4 mx-auto max-w-7xl">
        <div className="max-w-5xl mx-auto text-center flex flex-col items-center">
          
          {/* ============================================================== */}
          {/* STAGE 1: THE CROWN (Typography)                                */}
          {/* ============================================================== */}
          <motion.div
            initial={{ opacity: 0, y: 20 }}
            animate={{ opacity: 1, y: 0 }}
            transition={{ duration: 0.5, ease: "easeOut" }}
            className="mb-8"
          >
            {props.backLink ? (
               <Breadcrumb className="text-muted-foreground/40 inline-block backdrop-blur-md px-5 py-2.5 rounded-full border border-white/10 bg-black/40 shadow-xl">
               <BreadcrumbItem>
                 <BreadcrumbLink href={props.backLink.href} className="hover:text-white transition-colors flex items-center">
                   <ArrowLeft className="mr-2 h-3.5 w-3.5" />
                   {props.backLink.label}
                 </BreadcrumbLink>
               </BreadcrumbItem>
             </Breadcrumb>
            ) : (
              <Breadcrumb className="text-muted-foreground/40 inline-block backdrop-blur-md px-5 py-2.5 rounded-full border border-white/10 bg-black/40 shadow-xl">
                <BreadcrumbItem>
                  <BreadcrumbLink href="/guide" className="hover:text-white transition-colors">
                    Guides
                  </BreadcrumbLink>
                </BreadcrumbItem>
                <BreadcrumbSeparator />
                <BreadcrumbItem>
                  <BreadcrumbLink className="text-white font-medium">
                    {props.title.length > 30 ? props.title.slice(0, 30) + '...' : props.title}
                  </BreadcrumbLink>
                </BreadcrumbItem>
              </Breadcrumb>
            )}
          </motion.div>

          <motion.div
             initial={{ opacity: 0, y: 20 }}
             animate={{ opacity: 1, y: 0 }}
             transition={{ duration: 0.5, delay: 0.1, ease: "easeOut" }}
             className="flex flex-wrap justify-center items-center gap-3 mb-6"
          >
            {props.category && (
              <Badge className="bg-premium-cyan/10 text-premium-cyan border-premium-cyan/20 px-3 py-1 text-[11px] uppercase tracking-widest font-mono">
                {props.category}
              </Badge>
            )}
            {props.readTime && (
              <span className="text-sm text-slate-400 flex items-center gap-1.5 font-medium">
                <Clock className="w-3.5 h-3.5" />
                {props.readTime}
              </span>
            )}
            {props.publishedAt && (
              <span className="text-sm text-slate-400 flex items-center gap-1.5">
                <Calendar className="w-3.5 h-3.5" />
                {new Date(props.publishedAt).toLocaleDateString("en-US", { month: "long", day: "numeric", year: "numeric" })}
              </span>
            )}
            {props.updatedAt && (
              <span className="text-[11px] font-mono tracking-wider text-premium-cyan flex items-center gap-1.5 bg-premium-cyan/5 px-2.5 py-1 rounded-md border border-premium-cyan/10 uppercase">
                <CalendarCheck className="w-3 h-3" />
                Updated {new Date(props.updatedAt).toLocaleDateString("en-US", { month: "short", year: "numeric" })}
              </span>
            )}
          </motion.div>

          <motion.h1
             initial={{ opacity: 0, y: 30 }}
             animate={{ opacity: 1, y: 0 }}
             transition={{ duration: 0.7, delay: 0.2, ease: [0.16, 1, 0.3, 1] }}
             className="text-5xl md:text-6xl lg:text-7xl font-[family-name:var(--font-display)] font-semibold text-white mb-6 leading-tight tracking-normal bg-clip-text text-transparent bg-gradient-to-b from-white to-white/70 max-w-4xl"
          >
            {props.title}
          </motion.h1>

          {props.subtitle && (
            <motion.p
              initial={{ opacity: 0, y: 20 }}
              animate={{ opacity: 1, y: 0 }}
              transition={{ duration: 0.7, delay: 0.3, ease: [0.16, 1, 0.3, 1] }}
              className="text-xl md:text-2xl text-slate-400 font-light leading-relaxed max-w-3xl"
            >
              {props.subtitle}
            </motion.p>
          )}

          {/* ============================================================== */}
          {/* STAGE 2: THE CINEMA DISPLAY (Isolated Visual)                  */}
          {/* ============================================================== */}
          {props.coverImage && (
            <motion.div
              initial={{ opacity: 0, y: 40 }}
              animate={{ opacity: 1, y: 0 }}
              transition={{ duration: 1, delay: 0.4, ease: [0.16, 1, 0.3, 1] }}
              className="relative w-full aspect-[16/9] lg:aspect-[21/9] mt-12 md:mt-16 rounded-3xl overflow-hidden border border-white/10 shadow-[0_0_80px_rgba(8,145,178,0.15)] group transform-gpu"
            >
              <Image
                src={props.coverImage}
                alt={props.coverImageAlt || props.title}
                fill
                className="object-cover object-center transition-transform duration-[1.5s] group-hover:scale-[1.02] mix-blend-lighten"
                priority
                sizes="(max-width: 1024px) 100vw, 1024px"
              />
              {/* Inner glass reflection to amplify the 3D pane effect */}
              <div className="absolute inset-0 ring-1 ring-inset ring-white/10 rounded-3xl pointer-events-none mix-blend-overlay" />
            </motion.div>
          )}

          {/* ============================================================== */}
          {/* STAGE 3: THE FLOATING OVERLAP (TLDR)                           */}
          {/* ============================================================== */}
          {props.tldr && (
            <motion.div
              initial={{ opacity: 0, y: 20 }}
              animate={{ opacity: 1, y: 0 }}
              transition={{ duration: 0.6, delay: 0.6, ease: "easeOut" }}
              className={`relative z-20 ${
                props.coverImage ? "-mt-12 sm:-mt-20 shadow-[0_20px_60px_rgba(0,0,0,0.8)]" : "mt-12"
              } mx-auto max-w-3xl p-6 md:p-8 rounded-2xl bg-black/60 backdrop-blur-2xl border border-white/10 text-left overflow-hidden`}
            >
              {/* Holographic Top Edge */}
              <div className="absolute top-0 inset-x-0 h-px bg-gradient-to-r from-transparent via-premium-cyan/40 to-transparent" />
              
              <p className="text-xs font-mono font-bold uppercase tracking-widest text-premium-cyan mb-3 flex items-center gap-2">
                <span className="w-1.5 h-1.5 rounded-full bg-premium-cyan animate-pulse shadow-[0_0_8px_rgba(8,145,178,0.8)]" />
                Executive Summary
              </p>
              <p className="text-slate-300 leading-relaxed font-light text-lg">
                {props.tldr}
              </p>
            </motion.div>
          )}

        </div>
      </div>
    </div>
  )
}
